import type {
  AdminSession,
  AdminWorkshopOverview,
  AgendaResponse,
  BookingsResponse,
  CapacityResponse,
  ImportDetails,
  MeResponse,
  ResetBookingsResponse,
} from './types';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') ?? '';
  const data = contentType.includes('application/json') ? await response.json() : null;

  if (!response.ok) {
    throw new ApiError(data?.error ?? 'Anfrage fehlgeschlagen', response.status);
  }

  return data as T;
}

export async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  return parseResponse<T>(response);
}

export const api = {
  agenda: (day?: string) =>
    apiFetch<AgendaResponse>(day ? `/api/agenda?day=${encodeURIComponent(day)}` : '/api/agenda'),
  me: () => apiFetch<MeResponse>('/api/me'),
  myBookings: () => apiFetch<BookingsResponse>('/api/me/bookings'),
  participantLogin: (lastName: string, accessCode: string) =>
    apiFetch<{ attendee: MeResponse['attendee'] }>('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lastName, accessCode }),
    }),
  participantLogout: () =>
    apiFetch<{ ok: true }>('/api/auth/logout', {
      method: 'POST',
    }),
  bookSession: (sessionId: number) =>
    apiFetch<{ status: string }>('/api/bookings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    }),
  cancelSession: (sessionId: number) =>
    apiFetch<{ status: string }>(`/api/bookings/${sessionId}`, {
      method: 'DELETE',
    }),
  adminLogin: (username: string, password: string) =>
    apiFetch<AdminSession>('/api/admin/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    }),
  adminLogout: () =>
    apiFetch<{ ok: true }>('/api/admin/logout', {
      method: 'POST',
    }),
  adminSession: () => apiFetch<AdminSession>('/api/admin/session'),
  importProgram: (file: File, eventDate: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventDate', eventDate);

    return apiFetch<ImportDetails>('/api/admin/import-program', {
      method: 'POST',
      body: formData,
    });
  },
  importAttendees: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiFetch<ImportDetails>('/api/admin/import-attendees', {
      method: 'POST',
      body: formData,
    });
  },
  importDetails: (id: string) => apiFetch<ImportDetails>(`/api/admin/imports/${id}`),
  publishImport: (id: number) =>
    apiFetch<ImportDetails>(`/api/admin/imports/${id}/publish`, {
      method: 'POST',
    }),
  capacityOverview: () => apiFetch<CapacityResponse>('/api/admin/capacity-overview'),
  resetBookings: (eventDate?: string) =>
    apiFetch<ResetBookingsResponse>('/api/admin/bookings/reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ eventDate: eventDate ?? null }),
    }),
  adminWorkshops: () => apiFetch<AdminWorkshopOverview>('/api/admin/workshops'),
  createWorkshop: (payload: Record<string, unknown>) =>
    apiFetch<AdminWorkshopOverview>('/api/admin/workshops', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }),
  updateWorkshop: (id: number, payload: Record<string, unknown>) =>
    apiFetch<AdminWorkshopOverview>(`/api/admin/workshops/${id}/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }),
  deleteWorkshop: (id: number) =>
    apiFetch<{ ok: true }>(`/api/admin/workshops/${id}`, {
      method: 'DELETE',
    }),
  resetWorkshopBookings: (id: number) =>
    apiFetch<ResetBookingsResponse>(`/api/admin/workshops/${id}/bookings/reset`, {
      method: 'POST',
    }),
};
